import type { currentSurveySliceState } from "@survey-types/survey.core.types";
import type { LeaseCompMediaFile } from "@survey-types/survey.leaseComp.types";
import type {
  GetMediaByIdsResponse,
  MarketMediaFile,
  MediaFile,
  MediaOwner,
  PdfFileInfo,
} from "@survey-types/survey.media.types";
import {
  removeLeaseCompImage,
  setLeaseCompMedia,
  updateLeaseCompImageVisibility,
} from "@survey/leaseCompsSlice";
import {
  moveMedia,
  removeMedia,
  setPropertyMedia,
  updateMedia,
} from "@survey/surveySlice";
import { mediaApi } from "./base";
import { surveyCoreApi } from "./survey-core";

type MediaOwnerType = "PROPERTY" | "LEASE_COMP";

type OwnerMediaMutationPayload = {
  surveyId?: string;
  propertyId: string;
  mediaId: string;
  mediaType: string;
  ownerType?: MediaOwnerType;
  leaseCompId?: string;
};

type OwnerMediaReorderPayload = OwnerMediaMutationPayload & {
  order: string;
};

type OwnerMediaState = {
  survey?: currentSurveySliceState;
  leaseComps?: {
    items?: Array<{ leaseCompId: string }>;
    mediaFiles?: Record<string, { images: LeaseCompMediaFile[] }>;
  };
};

// Helper to normalize media type keys
const normalizeMediaType = (
  mediaType: string,
): "images" | "floorPlans" | "fliers" => {
  const typeMap: Record<string, "images" | "floorPlans" | "fliers"> = {
    images: "images",
    floorplans: "floorPlans",
    fliers: "fliers",
  };
  return (
    typeMap[mediaType.toLowerCase()] ||
    (mediaType.toLowerCase() as "images" | "floorPlans" | "fliers")
  );
};

const toLeaseCompMediaFile = (
  image: MediaFile,
  leaseCompId: string,
): LeaseCompMediaFile => ({
  ...image,
  leaseCompId,
});

type MediaHydrationState = {
  survey?: currentSurveySliceState;
  leaseComps?: {
    items?: Array<{ leaseCompId: string }>;
  };
};

export const mediaApiExtended = mediaApi.injectEndpoints({
  endpoints: (build) => ({
    getSurveyMediaFile: build.query<
      { media: MediaFile[] },
      { mediaType?: string; surveyId?: string }
    >({
      query: ({ mediaType, surveyId }) =>
        `api/media/${surveyId}/${mediaType}/active`,
    }),
    copySurveyMediaFiles: build.mutation<
      {
        success: boolean;
        media?: MediaFile[];
      },
      { MediaOwner: MediaOwner[] }
    >({
      query: ({ MediaOwner }) => ({
        url: `api/media/copyMediaData`,
        method: "POST",
        body: MediaOwner,
      }),
    }),
    getSurveyCoverImage: build.query<
      { media: MediaFile[] },
      { mediaType?: string }
    >({
      query: ({ mediaType }) => `api/media/${mediaType}`,
    }),
    deleteOwnerMediaFile: build.mutation<
      { success: boolean; media?: MediaFile[] },
      OwnerMediaMutationPayload
    >({
      query: ({ surveyId, propertyId, mediaId, mediaType, ownerType }) => ({
        url: `api/media`,
        method: "DELETE",
        body: {
          surveyId,
          propertyId,
          mediaId,
          mediaType,
          ...(ownerType ? { ownerType } : {}),
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        if (!arg.propertyId || !arg.mediaType || !arg.mediaId) return;

        if (arg.ownerType === "LEASE_COMP") {
          const state = getState() as OwnerMediaState;
          const leaseCompId = arg.leaseCompId ?? arg.propertyId;
          const originalImages =
            state.leaseComps?.mediaFiles?.[leaseCompId]?.images;

          dispatch(
            removeLeaseCompImage({
              leaseCompId,
              mediaId: arg.mediaId,
            }),
          );

          try {
            await queryFulfilled;
            // Update survey timestamp to enable PDF regeneration for lease comp media delete
            if (arg.surveyId) {
              dispatch(
                surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                  surveyId: arg.surveyId,
                }),
              );
            }
            // Lease comp media delete is scoped to leaseComps slice - no survey refetch needed
          } catch {
            if (originalImages) {
              dispatch(
                setLeaseCompMedia({
                  leaseCompId,
                  images: originalImages,
                }),
              );
            }
          }
          return;
        }

        const mediaTypeKey = normalizeMediaType(arg.mediaType);

        // Store original media for rollback
        const state = getState() as OwnerMediaState;
        const originalMedia = state.survey?.mediaFiles?.[arg.propertyId]?.[
          mediaTypeKey
        ]?.find((file: MediaFile) => file.mediaId === arg.mediaId);

        // Optimistically remove media
        dispatch(
          removeMedia({
            propertyId: arg.propertyId,
            mediaType: mediaTypeKey,
            mediaId: arg.mediaId,
          }),
        );
        const refreshResult = queryFulfilled;
        refreshResult
          .then(() => {
            // Update survey timestamp after successful deletion
            if (arg.surveyId) {
              dispatch(
                surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                  surveyId: arg.surveyId,
                }),
              );
            }
          })
          .catch(() => {
            // Rollback on failure
            if (originalMedia) {
              dispatch(
                updateMedia({
                  propertyId: arg.propertyId,
                  mediaType: mediaTypeKey,
                  media: [originalMedia],
                }),
              );
            }
          });
      },
    }),
    // used in AttachmentUploads and AttachmentThumbnailPreview components to rename or update media details
    updatePropertyMediaFile: build.mutation<
      { success: boolean; media?: MediaFile[] },
      MediaFile
    >({
      query: (mediaFileUpdate) => ({
        url: `api/media`,
        method: "PATCH",
        body: mediaFileUpdate,
      }),
      invalidatesTags: () => [{ type: "Media", id: "LIST" }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Update survey timestamp after successful update
          if (arg.surveyId) {
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId: arg.surveyId,
              }),
            );
          }
        } catch {
          // Error already handled by mutation
        }
      },
    }),
    updatePropertyMediaStatus: build.mutation<
      { success: boolean; media?: MediaFile[] },
      MediaFile
    >({
      query: (mediaFileStatusUpdate) => ({
        url: `api/media/status`,
        method: "PATCH",
        body: mediaFileStatusUpdate,
      }),
      invalidatesTags: () => [{ type: "Media", id: "LIST" }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Update survey timestamp after successful status update
          if (arg.surveyId) {
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId: arg.surveyId,
              }),
            );
          }
        } catch {
          // Error already handled by mutation
        }
      },
    }),
    updateLeaseCompMediaStatus: build.mutation<
      { success: boolean; media?: MediaFile[] },
      LeaseCompMediaFile
    >({
      query: (payload) => ({
        url: `api/media/status`,
        method: "PATCH",
        body: {
          mediaId: payload.mediaId,
          mediaType: payload.mediaType,
          propertyId: payload.leaseCompId,
          surveyId: payload.surveyId,
          isHidden: payload.isHidden,
          ownerType: "LEASE_COMP",
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistically update Redux so the preview reflects the change immediately
        dispatch(
          updateLeaseCompImageVisibility({
            leaseCompId: arg.leaseCompId,
            mediaId: arg.mediaId,
            isHidden: arg.isHidden ?? false,
          }),
        );
        try {
          await queryFulfilled;
          // Update survey timestamp to enable PDF regeneration
          dispatch(
            surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
              surveyId: arg.surveyId,
            }),
          );
        } catch {
          // Rollback Redux state on API failure
          dispatch(
            updateLeaseCompImageVisibility({
              leaseCompId: arg.leaseCompId,
              mediaId: arg.mediaId,
              isHidden: !arg.isHidden,
            }),
          );
        }
      },
    }),
    uploadOwnerMediaFile: build.mutation<MediaFile, FormData>({
      query: (mediaFileFormData) => ({
        url: `api/media/uploadFile`,
        method: "POST",
        body: mediaFileFormData,
      }),
      // Components handle optimistic updates for both property and lease comp uploads.
      async onQueryStarted(mediaFileFormData, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // The "media" field is a JSON string containing surveyId, see mediaUpload() below
          const mediaMeta = mediaFileFormData.get("media");
          const surveyId =
            typeof mediaMeta === "string"
              ? (JSON.parse(mediaMeta) as { surveyId?: string }).surveyId
              : undefined;
          if (surveyId) {
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId,
              }),
            );
          }
        } catch {
          // Error already handled by mutation
        }
      },
    }),
    uploadMarketMapFile: build.mutation<MarketMediaFile, FormData>({
      query: (mediaMarketMapFileFormData) => ({
        url: `api/media/uploadMarketMap`,
        method: "POST",
        body: mediaMarketMapFileFormData,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      extraOptions: { maxRetries: 2 },
    }),
    getPdfUrl: build.query<Array<PdfFileInfo>, { surveyId: string }>({
      query: ({ surveyId }) => `api/fileGeneration/survey/${surveyId}`,
    }),
    generatePdf: build.mutation<
      Array<PdfFileInfo>,
      {
        surveyId: string;
        isLandscape: boolean;
        name: string;
        sectionInfoList?: Array<{ name: string; link: string }>;
      }
    >({
      query: ({ surveyId, isLandscape, name, sectionInfoList }) => ({
        url: `api/fileGeneration/processPdfAsync`,
        method: "POST",
        body: {
          isLandscape,
          name,
          surveyId,
          ...(sectionInfoList && { sectionInfoList }),
        },
      }),
    }),
    getMediaByIds: build.query<
      GetMediaByIdsResponse,
      { ids: string[]; ownerType?: MediaOwnerType }
    >({
      query: ({ ids, ownerType }) => ({
        url: `api/media/getMediaByIds`,
        method: "POST",
        body: {
          ids,
          ...(ownerType ? { ownerType } : {}),
        },
      }),
      providesTags: (result, _error, { ids }) =>
        result
          ? [
              ...ids.map((id: string) => ({ type: "Media" as const, id })),
              { type: "Media", id: "LIST" },
            ]
          : [{ type: "Media", id: "LIST" }],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const shouldHydratePropertyMedia = arg.ownerType !== "LEASE_COMP";
          const shouldHydrateLeaseCompMedia = arg.ownerType !== "PROPERTY";

          if (shouldHydratePropertyMedia && data?.propertyMedia) {
            // Use setPropertyMedia to update the entire propertyMedia object efficiently
            dispatch(setPropertyMedia(data.propertyMedia));
          }

          // Hydrate lease comp media in unified leaseComps slice when available.
          if (shouldHydrateLeaseCompMedia && data?.leaseCompMedia) {
            Object.entries(data.leaseCompMedia).forEach(
              ([leaseCompId, mediaGroup]) => {
                dispatch(
                  setLeaseCompMedia({
                    leaseCompId,
                    images: mediaGroup.images ?? [],
                  }),
                );
              },
            );
          }

          // Also hydrate lease comp media when the API returns lease comp owners
          // inside the property-style payload keyed by owner ID.
          if (shouldHydrateLeaseCompMedia && data?.propertyMedia) {
            const state = getState() as MediaHydrationState;
            const leaseCompIds = new Set(
              (state.leaseComps?.items ?? []).map((item) => item.leaseCompId),
            );

            Object.entries(data.propertyMedia).forEach(
              ([ownerId, mediaGroup]) => {
                if (!leaseCompIds.has(ownerId)) {
                  return;
                }

                const leaseCompImages = (mediaGroup.images ?? []).map((image) =>
                  toLeaseCompMediaFile(image, ownerId),
                );

                dispatch(
                  setLeaseCompMedia({
                    leaseCompId: ownerId,
                    images: leaseCompImages,
                  }),
                );
              },
            );
          }
        } catch (_error) {
          // Handle error if needed
          console.error("Failed to fetch media by IDs:");
        }
      },
    }),
    getReducedPdf: build.query<MediaFile, { mediaId: string }>({
      query: ({ mediaId }) => `api/media/reducePdf/${mediaId}`,
    }),
    getEdpTransformedImage: build.query<Blob, { imageUrl: string }>({
      query: ({ imageUrl }) => ({
        url: `api/media/edp/attachment?filename=${encodeURIComponent(imageUrl)}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),
    reorderOwnerMedia: build.mutation<
      { media: MediaFile[] },
      OwnerMediaReorderPayload
    >({
      query: ({
        surveyId,
        propertyId,
        mediaType,
        mediaId,
        order,
        ownerType,
      }) => ({
        url: `api/media/reorder`,
        method: "PATCH",
        body: {
          surveyId,
          propertyId,
          mediaType,
          mediaId,
          order,
          ...(ownerType ? { ownerType } : {}),
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Media", id: arg.propertyId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Keep survey timestamp updates for property media reorder only.
          // Lease comp reorders are isolated and do not require a survey refetch.
          if (arg.surveyId && arg.ownerType !== "LEASE_COMP") {
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId: arg.surveyId,
              }),
            );
          }
        } catch {
          // Error already handled by mutation
        }
      },
    }),
    moveOwnerMedia: build.mutation<
      { success: boolean; media?: MediaFile[] },
      {
        surveyId: string;
        propertyId: string;
        mediaId: string;
        fromType: "images" | "floorPlans" | "fliers";
        toType: "images" | "floorPlans" | "fliers";
      }
    >({
      query: ({ surveyId, propertyId, mediaId, toType }) => ({
        url: `api/media`,
        method: "PATCH",
        body: {
          surveyId,
          propertyId,
          mediaId,
          mediaType: toType.toUpperCase(),
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistically move the file between sections in Redux
        dispatch(
          moveMedia({
            propertyId: arg.propertyId,
            fromType: arg.fromType,
            toType: arg.toType,
            mediaId: arg.mediaId,
          }),
        );
        try {
          await queryFulfilled;
          if (arg.surveyId) {
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId: arg.surveyId,
              }),
            );
          }
        } catch {
          // Rollback the move on failure
          dispatch(
            moveMedia({
              propertyId: arg.propertyId,
              fromType: arg.toType,
              toType: arg.fromType,
              mediaId: arg.mediaId,
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useGetSurveyMediaFileQuery,
  useCopySurveyMediaFilesMutation,
  useGetSurveyCoverImageQuery,
  useDeleteOwnerMediaFileMutation,
  useUploadOwnerMediaFileMutation,
  useUploadMarketMapFileMutation,
  useGetPdfUrlQuery,
  useGeneratePdfMutation,
  useUpdatePropertyMediaFileMutation,
  useUpdatePropertyMediaStatusMutation,
  useUpdateLeaseCompMediaStatusMutation,
  useReorderOwnerMediaMutation,
  useMoveOwnerMediaMutation,
  useGetMediaByIdsQuery,
  useGetReducedPdfQuery,
  useGetEdpTransformedImageQuery,
} = mediaApiExtended;

export const useDeleteLeaseCompImageMutation = useDeleteOwnerMediaFileMutation;
export const useUploadLeaseCompImageMutation = useUploadOwnerMediaFileMutation;
export const useReorderLeaseCompImagesMutation = useReorderOwnerMediaMutation;

/**
 * Uploads a media file to the server for a given survey and media type.
 * @param file File object to upload
 * @param surveyId Survey ID
 * @param mediaType Media type string (e.g., 'CLIENTLOGO')
 * @returns Promise<MediaFile> - uploaded media file object
 */
export async function mediaUpload(
  file: File,
  surveyId: string,
  mediaType: string,
  uploadMutation: ReturnType<typeof useUploadOwnerMediaFileMutation>[0],
): Promise<MediaFile> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append(
    "media",
    JSON.stringify({
      surveyId,
      user: localStorage.getItem("email") || "",
      mediaType,
    }),
  );
  return await uploadMutation(formData).unwrap();
}
